import { Checkbox } from "@/components/ui/Checkbox";
import { Trash2 } from "lucide-react";
import type { RepoPaperRow } from "@/features/repos/api/paperRepos";

/** Compact display for Semantic Scholar citation counts (when present). */
const formatCitations = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  return Intl.NumberFormat("en-US").format(n);
}

export const PaperRow = ({
  paper,
  selected,
  detailActive,
  onToggleSelect,
  onRowClick,
  onDelete,
  showDelete = true,
  showCitationCount = false,
}: {
  paper: RepoPaperRow;
  selected: boolean;
  detailActive: boolean;
  onToggleSelect: () => void;
  onRowClick: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  showCitationCount?: boolean;
}) => {
  const firstAuthor = paper.authors?.split(";")[0]?.split(" and ")[0]?.trim() ?? "";
  const extraAuthors = (paper.authors?.split(";").length ?? 1) - 1;
  const addedDate = new Date(paper.created_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const cites =
    showCitationCount ?
      paper.cited_by_count == null ?
        "—"
        : formatCitations(paper.cited_by_count)
      : null;

  return (
    <div
      className={`border-b border-zinc-100 last:border-b-0 ${selected ? "bg-zinc-50" : ""
        } ${detailActive ? "bg-blue-50" : ""}`}
    >
      <div
        className={`grid items-center gap-2 px-2.5 py-3 hover:bg-zinc-50/50 transition-colors ${showCitationCount ?
          "grid-cols-[40px_1fr_120px_48px_48px_112px_48px]"
          : "grid-cols-[40px_1fr_140px_60px_140px_48px]"
          }`}
      >
        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onCheckedChange={onToggleSelect} />
        </div>
        <button className="text-left min-w-0" type="button" onClick={onRowClick}>
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${paper.paper_id ? "bg-emerald-400" : "bg-zinc-300"}`}
              title={paper.paper_id ? "Corpus-linked" : "Custom entry"}
            />
            <p className="text-sm font-medium text-zinc-900 truncate">{paper.title}</p>
          </div>
          {paper.journal && (
            <p className="text-xs text-zinc-400 truncate mt-0.5 ml-3">{paper.journal}</p>
          )}
        </button>
        <div className="text-sm text-zinc-600 truncate">
          {firstAuthor}
          {extraAuthors > 0 && (
            <span className="text-zinc-400"> +{extraAuthors}</span>
          )}
        </div>
        {showCitationCount && (
          <div className="text-xs tabular-nums text-zinc-500 text-right" title="Citations">
            {cites}
          </div>
        )}
        <div className="text-sm text-zinc-600 tabular-nums">{paper.year ?? "—"}</div>
        <div className="text-xs text-zinc-400">{addedDate}</div>
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          {showDelete && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Remove from repository"
              title="Remove from repository"
              className="p-1.5 rounded-[3px] text-zinc-400 hover:text-red-700 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
